/**
 * Creates a table from the response of a query using listData.json service
 */

import {
    Component,
    Input,
    ViewEncapsulation,
    signal,
    OnInit,
    ChangeDetectionStrategy,
    ViewChild,
    Signal,
} from "@angular/core";
import { ListData } from "../../pojo/ListData";
import { Table } from "../s25-table/Table";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25TableModule } from "../s25-table/s25.table.module";
import { Bind } from "../../decorators/bind.decorator";
import { ButtonClassSuffix, S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { ListDataParams2, ListService } from "../../services/list.service";
import { S25Util } from "../../util/s25-util";
import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";

@TypeManagerDecorator("s25-ng-list-data")
@Component({
    selector: "s25-ng-list-data",
    template: `
        @if (init()) {
            <ng-container *ngTemplateOutlet="actionsTemplate"></ng-container>
            <s25-ng-table
                [columnSortable]="true"
                [dataSource]="tableConfig"
                [unlimitedWidth]="true"
                [pivotThreshold]="0"
                [hasTotalRowCount]="false"
                [hasSelect]="hasSelect"
                [hasSelectAll]="hasSelect"
                [(selected)]="selectedItems"
            >
            </s25-ng-table>
            <ng-container [ngTemplateOutlet]="actionsTemplate"></ng-container>

            <ng-template #actionsTemplate>
                @for (action of actions ?? []; track action.text()) {
                    <s25-ng-button
                        [type]="action.type"
                        [disabled]="action.activateOnChecked && selectedItems.size === 0"
                        [buttonText]="action.text()"
                        [title]="action.subText()"
                        [buttonClass]="action.classList || ''"
                        (click)="action.action(tableComponent)"
                    ></s25-ng-button>
                }
            </ng-template>
        }
    `,
    styles: ``,
    standalone: true,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25TableModule, S25ButtonComponent, CommonModule, NgTemplateOutlet],
})
export class S25ListDataComponent implements OnInit {
    @Input({ required: true }) query: ListDataParams2;
    @Input() hasSelect: boolean = false;
    @Input() actions: Action[] = [];
    @Input() tableConfig: Table.DataSource;

    init = signal(false);

    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;
    selectedItems = new Set<Table.Row["id"]>();

    dateFormat: string;
    timeFormat: string;
    dateTimeFormat: string;

    async ngOnInit() {
        const [dateFormat, timeFormat, dateTimeFormat] = await S25Util.all([
            UserprefService.getS25Dateformat(),
            UserprefService.getS25Timeformat(),
            UserprefService.getS25DateTimeformat(),
        ]);

        this.dateFormat = dateFormat;
        this.timeFormat = timeFormat;
        this.dateTimeFormat = dateTimeFormat;

        this.buildTableConfig();
        this.init.set(true);
    }

    buildTableConfig() {
        this.tableConfig = {
            ...this.tableConfig,
            type: this.tableConfig?.type ?? "paginated",
            dataSource: this.getDataPaginated.bind(this),
            startPage: this.query.page || 0,
            defaultPageSize: this.query.itemsPerPage ?? 25,
            pageSizes: [25, 50, 100],
            columns: this.tableConfig?.columns ?? [],
            customRowCount: this.getRowCount,
            hasMorePages: () => false, //TODO: implement has more pages if when needed
        };
    }

    @Bind
    getRowCount(counts: Table.RowCounts) {
        const type = S25Util.firstCharToUpper(this.query.itemType) + (counts.total === 1 ? "" : "s");
        return `${counts.total} Matching ${type}`;
    }

    constructor() {}

    setColumnsFromListData(listResp: ListData.Wrapper) {
        let columns: Table.Column[] = listResp.cols.map((column) => ({
            id: column.prefname,
            header: column.name,
            sortable: column.sortable === 1,
            type: !!column.isDate ? "date" : undefined,
        }));

        this.tableComponent.setColumns(columns);
    }

    async getDataFromService(query: Table.PaginatedQuery): Promise<ListData.Wrapper> {
        let listQuery: ListDataParams2 = {
            itemType: this.query.itemType,
            query: this.query.query,
            page: query.page + 1, //List service is 1 based
            sort: { column: query.sortColumn.id, order: query.sortColumn.order || "asc" },
            useCache: !query.forceRefresh,
            cacheId: +query.cacheId || 0,
            comptype: this.query.comptype,
            itemsPerPage: query.pageSize || this.query.itemsPerPage,
        };
        return await ListService.getData2(listQuery);
    }

    @Bind
    async getDataPaginated(query: Table.PaginatedQuery): Promise<Table.DataSourceResponse> {
        const [resp, err] = await S25Util.Maybe(this.getDataFromService(query));
        let retVal = this.listToTable(resp);
        this.setColumnsFromListData(resp);
        retVal.totalRows = resp?.rows[0]?.count || 1;
        return retVal;
    }

    listToTable(listResp: ListData.Wrapper): Table.DataSourceResponse {
        const rows: Table.Row[] = listResp.rows.map((rowWrapper) => ({
            id: rowWrapper.contextId,
            name: rowWrapper.row[0], // Assuming the first element is the name
            cells: rowWrapper.row.reduce(
                (acc, row, index) => {
                    if (!listResp.cols[index]) {
                        //If the column is not defined by table drop it
                        return acc;
                    }
                    //parse date if needed
                    if (listResp.cols[index].isDate) {
                        acc[listResp.cols[index].prefname] = {
                            text: S25Datefilter.transform(S25Util.date.parse(row), this.dateFormat),
                        };
                    } else if (listResp.cols[index].isTime) {
                        acc[listResp.cols[index].prefname] = {
                            text: S25Datefilter.transform(S25Util.date.parse(row), this.timeFormat),
                        };
                    } else if (listResp.cols[index].isDateTime) {
                        acc[listResp.cols[index].prefname] = {
                            text: S25Datefilter.transform(S25Util.date.parse(row), this.dateTimeFormat),
                        };
                    } else {
                        acc[listResp.cols[index].prefname] = { text: row };
                    }

                    return acc;
                },
                {} as Record<string, Table.Cell>,
            ),
        }));
        return { rows };
    }
}

export type Action = {
    type: ButtonClassSuffix;
    text: Signal<string>;
    subText: Signal<string>;
    activateOnChecked: boolean;
    action: (table: S25TableComponent) => Promise<void>;
    classList?: string;
};
